import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowRight, Clock, Factory, HelpCircle, LogIn, UserPlus, CheckCircle2 } from 'lucide-react';
import LanguageSwitcher from '@/components/LanguageSwitcher';

/**
 * Login + Register page — minimalist premium (Stripe / Linear inspired).
 * - Calm dark canvas (zinc-950) with generous breathing room
 * - Single editorial photo of an industrial workshop on the right
 * - Form on the left with light borders, no heavy animations
 */

const HERO_IMAGE_URL =
  'https://images.unsplash.com/photo-1711418235334-8895331a6cf9?auto=format&fit=crop&w=1600&q=80';

export default function LoginPage() {
  const { login, register } = useAuth();
  const { t } = useTranslation();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'login') {
        const result = await login(email, password);
        if (result?.pending_approval) {
          setPending({ email: result.email });
          setLoading(false);
          return;
        }
      } else {
        if (password.length < 6) { setError('Mot de passe : 6 caractères minimum'); setLoading(false); return; }
        if (!companyName.trim() || !name.trim()) { setError('Société et contact requis'); setLoading(false); return; }
        await register({ email, password, name, company_name: companyName, phone, role: 'client' });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      if (status === 403) {
        setError(detail || "Accès révoqué. Contactez : prodlinkfr@gmail.com");
      } else if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map(e => e.msg || JSON.stringify(e)).join(' '));
      } else {
        setError(mode === 'login' ? 'Erreur de connexion' : "Erreur d'inscription");
      }
    } finally {
      setLoading(false);
    }
  };

  // ===== Pending approval state =====
  if (pending) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center p-6" data-testid="pending-approval-screen">
        <div className="absolute top-5 right-6"><LanguageSwitcher /></div>
        <div className="w-full max-w-md bg-zinc-900/40 border border-zinc-800 rounded-md p-8 text-center">
          <div className="inline-flex w-12 h-12 rounded-full bg-zinc-800 border border-zinc-700 items-center justify-center mb-5">
            <Clock className="w-5 h-5 text-amber-400" />
          </div>
          <h2 className="text-xl font-semibold mb-2 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            {t('pending.title')}
          </h2>
          <p className="text-xs text-zinc-500 mb-1">{pending.email}</p>
          <p className="text-sm text-zinc-400 mb-7 leading-relaxed">{t('pending.desc')}</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setPending(null)} className="flex-1 border-zinc-800 text-zinc-300 hover:bg-zinc-800/60 rounded-md" data-testid="pending-back-btn">
              {t('pending.logout')}
            </Button>
            <Button onClick={() => { setPending(null); setError(''); }} className="flex-1 bg-zinc-100 text-zinc-950 hover:bg-white rounded-md font-medium" data-testid="pending-retry-btn">
              {t('pending.refresh')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ===== Main login / register =====
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex" data-testid="login-page">
      <div className="absolute top-5 right-6 z-30"><LanguageSwitcher /></div>

      {/* LEFT — clean form */}
      <div className="flex-1 flex items-center justify-center px-6 sm:px-10 py-10 lg:py-16">
        <div className="w-full max-w-sm">
          {/* Brand */}
          <Link to="/login" className="inline-flex items-center gap-2.5 mb-10 group" data-testid="brand-link">
            <img
              src={`${process.env.PUBLIC_URL || ''}/prodpilot-logo.png`}
              alt="ProdPilot"
              className="w-8 h-8 rounded-sm object-contain"
            />
            <span className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              <span className="text-zinc-50">Prod</span><span className="text-blue-400 group-hover:text-blue-300 transition-colors">Pilot</span>
            </span>
          </Link>

          {/* Mode segmented control */}
          <div className="inline-flex p-0.5 bg-zinc-900/60 border border-zinc-800 rounded-full mb-8 text-xs">
            <button
              data-testid="tab-login"
              onClick={() => { setMode('login'); setError(''); }}
              className={`px-4 py-1.5 rounded-full transition-all ${mode === 'login' ? 'bg-zinc-100 text-zinc-950 font-medium' : 'text-zinc-400 hover:text-zinc-200'}`}
            >
              <LogIn className="w-3 h-3 inline mr-1" /> {t('login.title')}
            </button>
            <button
              data-testid="tab-register"
              onClick={() => { setMode('register'); setError(''); }}
              className={`px-4 py-1.5 rounded-full transition-all ${mode === 'register' ? 'bg-zinc-100 text-zinc-950 font-medium' : 'text-zinc-400 hover:text-zinc-200'}`}
            >
              <UserPlus className="w-3 h-3 inline mr-1" /> {t('login.signup_client')}
            </button>
          </div>

          {/* Title */}
          <h1 className="text-3xl font-semibold tracking-tight text-zinc-50 mb-1.5 leading-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            {mode === 'login' ? t('login.welcome') : t('login.welcome_register')}
          </h1>
          <p className="text-sm text-zinc-500 mb-8">
            {mode === 'login' ? t('login.welcome_sub') : t('login.welcome_register_sub')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            {error && (
              <div className="text-xs text-rose-400 bg-rose-500/5 border border-rose-500/20 rounded-md px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}

            {mode === 'register' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-zinc-400">Société</Label>
                    <Input
                      data-testid="reg-company"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      required
                      placeholder="ACME SARL"
                      className="bg-transparent border-zinc-800 hover:border-zinc-700 focus-visible:border-zinc-600 focus-visible:ring-0 text-zinc-100 rounded-md h-10 text-sm"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-zinc-400">Contact</Label>
                    <Input
                      data-testid="reg-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      placeholder="Jean Dupont"
                      className="bg-transparent border-zinc-800 hover:border-zinc-700 focus-visible:border-zinc-600 focus-visible:ring-0 text-zinc-100 rounded-md h-10 text-sm"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-zinc-400">Téléphone</Label>
                  <Input
                    data-testid="reg-phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="06 12 34 56 78"
                    className="bg-transparent border-zinc-800 hover:border-zinc-700 focus-visible:border-zinc-600 focus-visible:ring-0 text-zinc-100 rounded-md h-10 text-sm"
                  />
                </div>
              </>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-zinc-400">{t('login.email')}</Label>
              <Input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="vous@entreprise.com"
                className="bg-transparent border-zinc-800 hover:border-zinc-700 focus-visible:border-zinc-600 focus-visible:ring-0 text-zinc-100 rounded-md h-10 text-sm"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-zinc-400">{t('login.password')}</Label>
              <Input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder={mode === 'register' ? '6 caractères minimum' : '••••••••'}
                className="bg-transparent border-zinc-800 hover:border-zinc-700 focus-visible:border-zinc-600 focus-visible:ring-0 text-zinc-100 rounded-md h-10 text-sm"
              />
            </div>

            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full bg-zinc-100 hover:bg-white text-zinc-950 font-medium h-10 rounded-md transition-colors mt-2 group"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-zinc-950 border-t-transparent rounded-full animate-spin" />
              ) : (
                <span className="inline-flex items-center">
                  {mode === 'login' ? t('login.submit') : t('login.submit_register')}
                  <ArrowRight className="w-3.5 h-3.5 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
                </span>
              )}
            </Button>
          </form>

          {/* Public links */}
          <div className="mt-8 pt-6 border-t border-zinc-900 flex flex-col gap-2">
            <Link
              to="/register-subcontractor"
              className="flex items-center justify-between text-xs px-1 py-2 text-zinc-400 hover:text-zinc-100 transition-colors group"
              data-testid="link-register-sub"
            >
              <span className="flex items-center gap-2">
                <Factory className="w-3.5 h-3.5 text-zinc-500 group-hover:text-blue-400 transition-colors" />
                {t('login.signup_sub')}
              </span>
              <ArrowRight className="w-3 h-3 text-zinc-700 group-hover:text-zinc-400 group-hover:translate-x-0.5 transition-all" />
            </Link>
            <Link
              to="/how-it-works"
              className="flex items-center justify-between text-xs px-1 py-2 text-zinc-400 hover:text-zinc-100 transition-colors group"
              data-testid="link-how-it-works"
            >
              <span className="flex items-center gap-2">
                <HelpCircle className="w-3.5 h-3.5 text-zinc-500 group-hover:text-blue-400 transition-colors" />
                {t('login.how')}
              </span>
              <ArrowRight className="w-3 h-3 text-zinc-700 group-hover:text-zinc-400 group-hover:translate-x-0.5 transition-all" />
            </Link>
          </div>

          <p className="mt-10 text-[11px] text-zinc-600">
            © 2026 ProdPilot · Suivi. Production. Performance.
          </p>
          <p className="mt-2 text-[11px] text-zinc-600">
            <Link
              to="/politique-de-confidentialite"
              className="hover:text-amber-400 transition-colors"
              data-testid="login-privacy-link"
            >
              Politique de confidentialité
            </Link>
          </p>
        </div>
      </div>

      {/* RIGHT — editorial photo */}
      <div
        className="hidden lg:block flex-1 relative bg-zinc-900 overflow-hidden"
        data-testid="login-hero"
      >
        <img
          src={HERO_IMAGE_URL}
          alt="Atelier d'usinage CNC en production"
          className="absolute inset-0 w-full h-full object-cover opacity-80"
          loading="eager"
        />
        {/* Top-to-bottom dark fade for legibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/20 to-zinc-950/40" />
        {/* Left edge fade so the photo blends into the form panel */}
        <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-zinc-950 to-transparent" />

        {/* Bottom-left editorial caption */}
        <div className="absolute bottom-12 left-12 right-12 max-w-md">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-zinc-100/10 backdrop-blur-sm border border-zinc-100/15 rounded-full mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[11px] uppercase tracking-wider text-zinc-100 font-medium">
              Réseau industriel collaboratif
            </span>
          </div>
          <h2 className="text-3xl xl:text-4xl font-semibold tracking-tight text-zinc-50 leading-tight mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Vos pièces.<br />Votre réseau.<br />Au même endroit.
          </h2>
          <p className="text-sm text-zinc-200/90 leading-relaxed mb-6 max-w-sm">
            Donneurs d'ordres et ateliers d'usinage connectés : demandez vos pièces, suivez la production et discutez en direct.
          </p>

          {/* Trust ticks — client + subcontractor value props */}
          <ul className="space-y-1.5 text-sm text-zinc-100">
            <li className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              <span>Demandes de prix en ligne &amp; suivi de production temps réel</span>
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              <span>Devis, paiements et livraisons centralisés</span>
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              <span>Messagerie directe avec votre atelier ou vos clients</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
